// Y mapping is art-style-specific. Each style maps normalised price ph ∈ [0,1] to a
// canvas-fraction pair { bottom, top } where:
//   svgY = VH * (bottom - (bottom - top) * ph)
//   ph=0 (min close) → svgY = VH * bottom
//   ph=1 (max close) → svgY = VH * top
//
// Derivations:
//   Mountain Range : p.lerp(height*0.72, height*0.14, ph)         → bottom=0.72, top=0.14
//   Moon Base GLSL : mix(-0.30, 0.70, ph) → svgY=VH*(0.65-0.5*ph) → bottom=0.65, top=0.15
//
// X mapping: candle i → svgX = (i / (N-1)) * VW  (edge-to-edge, matching both styles)

const VW = 1600;
const VH = 900;

// Per-art-style price Y range (bottom and top as fractions of canvas height)
const PRICE_Y_RANGES = {
    'Mountain Range': { bottom: 0.72, top: 0.14 },
    'Moon Base': { bottom: 0.65, top: 0.15 },
    'Constellation Map': { bottom: 0.85, top: 0.15 },
    // City Skyline: CS_TOP_MAX=0.84 (min price), CS_TOP_MIN=0.52 (max price); ground at 0.92
    'City Skyline': { bottom: 0.84, top: 0.52 },
    // Kowloon: CITY_TOP_MAX=0.92 (min price), CITY_TOP_MIN=0.02 (max price); ground at 1.00
    Kowloon: { bottom: 0.92, top: 0.02 },
    // Other styles don't have a single price-mountain ridge; use Mountain Range as default
    default: { bottom: 0.72, top: 0.14 },
};

const BULLISH = '#26a69a';
const BEARISH = '#ef5350';
const LABEL_COLOR = 'rgba(255,255,255,0.5)';
const GRID_COLOR = 'rgba(255,255,255,0.06)';
const FONT = "'Press Start 2P', monospace";

const formatPrice = (price) => {
    if (price >= 10000) return price.toFixed(0);
    if (price >= 100) return price.toFixed(1);
    if (price >= 1) return price.toFixed(2);
    if (price >= 0.01) return price.toFixed(4);
    return price.toFixed(6);
};

const CandlestickChart = ({ chartData, artStyleName, visible }) => {
    if (!chartData?.values?.length) return null;

    const values = chartData.values;
    const N = values.length;

    const closes = values.map((v) => v.close);
    const minClose = Math.min(...closes);
    const maxClose = Math.max(...closes);
    const closeRange = maxClose - minClose || 1;

    const { bottom, top } =
        PRICE_Y_RANGES[artStyleName] ?? PRICE_Y_RANGES.default;

    // Convert any price to SVG Y using the active art style's mountain mapping
    const priceToY = (price) => {
        const ph = (price - minClose) / closeRange;
        return VH * (bottom - (bottom - top) * ph);
    };

    // Candle center X: edge-to-edge, matching samplePrice t = i / (N-1)
    const candleX = (i) => (N > 1 ? (i / (N - 1)) * VW : VW / 2);

    const slotW = N > 1 ? VW / (N - 1) : VW;
    const bodyW = Math.max(Math.min(slotW * 0.55, 18), 1.5);
    const wickW = Math.max(bodyW * 0.2, 1);

    // 5 price grid levels across the close range
    const gridLevels = Array.from(
        { length: 5 },
        (_, i) => minClose + (closeRange / 4) * i
    );

    // ~5 date labels evenly spaced
    const dateCount = Math.min(5, N);
    const dateIndices = Array.from({ length: dateCount }, (_, i) =>
        Math.round((i / (dateCount - 1)) * (N - 1))
    );

    return (
        <div className={`candlestick-overlay${visible ? ' is-visible' : ''}`}>
            <svg
                viewBox={`0 0 ${VW} ${VH}`}
                preserveAspectRatio="none"
                width="100%"
                height="100%"
            >
                {/* Semi-transparent background */}
                <rect
                    x="0"
                    y="0"
                    width={VW}
                    height={VH}
                    fill="rgba(0,0,8,0.80)"
                />

                {/* Horizontal grid lines at each price level */}
                {gridLevels.map((price, i) => {
                    const y = priceToY(price);
                    return (
                        <line
                            key={i}
                            x1="0"
                            y1={y}
                            x2={VW}
                            y2={y}
                            stroke={GRID_COLOR}
                            strokeWidth="1"
                        />
                    );
                })}

                {/* Candles */}
                {values.map((v, i) => {
                    const cx = candleX(i);
                    const isBull = v.close >= v.open;
                    const color = isBull ? BULLISH : BEARISH;
                    const bodyTop = priceToY(Math.max(v.open, v.close));
                    const bodyBottom = priceToY(Math.min(v.open, v.close));
                    const bodyH = Math.max(bodyBottom - bodyTop, 1);

                    return (
                        <g key={i}>
                            <line
                                x1={cx}
                                y1={priceToY(v.high)}
                                x2={cx}
                                y2={priceToY(v.low)}
                                stroke={color}
                                strokeWidth={wickW}
                            />
                            <rect
                                x={cx - bodyW / 2}
                                y={bodyTop}
                                width={bodyW}
                                height={bodyH}
                                fill={color}
                            />
                        </g>
                    );
                })}

                {/* Price axis labels — right of mountain range */}
                {gridLevels.map((price, i) => {
                    const y = priceToY(price);
                    return (
                        <text
                            key={i}
                            x={VW - 10}
                            y={y + 5}
                            fill={LABEL_COLOR}
                            fontSize="14"
                            fontFamily={FONT}
                            textAnchor="end"
                        >
                            {formatPrice(price)}
                        </text>
                    );
                })}

                {/* Date labels just below the mountain bottom */}
                {dateIndices.map((idx, i) => (
                    <text
                        key={idx}
                        x={candleX(idx)}
                        y={VH * bottom + 32}
                        fill={LABEL_COLOR}
                        fontSize="12"
                        fontFamily={FONT}
                        textAnchor={
                            i === 0
                                ? 'start'
                                : i === dateIndices.length - 1
                                  ? 'end'
                                  : 'middle'
                        }
                    >
                        {values[idx].datetime.substring(0, 10)}
                    </text>
                ))}
            </svg>
        </div>
    );
};

export default CandlestickChart;
